/**
 * Helper: Retry
 * -----------------------------------------------------------------------------
 * Retry an async function with exponential backoff, for network calls that
 * are prone to transient failures (flaky connection, brief server hiccup).
 *
 */

/**
 * Retry an async function with exponential backoff.
 * @param {Function} fn - Async function to retry, called with no arguments.
 * @param {Object} [options] - Options object.
 * @param {Number} [options.retries] - Max retry attempts after the first try.
 * @param {Number} [options.delay] - Base delay in ms, doubled each retry.
 * @returns {Promise}
 */
export async function retryWithBackoff(fn, { retries = 2, delay = 300 } = {}) {
  let attempt = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (error) {
      attempt += 1;

      if (attempt > retries) {
        throw error;
      }

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, delay * 2 ** (attempt - 1));
      });
    }
  }
}
